import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();
const STORAGE_KEY = 'sdny_auth_v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setUser(parsed.user);
        setToken(parsed.token);
      }
    } catch (e) {
      console.warn('Failed to load auth', e);
    } finally {
      setLoading(false);
    }

    // Listen for token expiry events from API interceptor
    const handleExpired = () => {
      clearAuth();
      toast.error('Session expired. Please log in again.');
    };
    window.addEventListener('sdny_auth_expired', handleExpired);
    return () => window.removeEventListener('sdny_auth_expired', handleExpired);
  }, [clearAuth]);

  useEffect(() => {
    if (!loading && user && token) {
      // Persist both access token and refresh token
      const raw = localStorage.getItem(STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : {};
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        user,
        token,
        refresh_token: existing.refresh_token || null,
      }));
    }
  }, [user, token, loading]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/auth/login`, { email, password });
      setUser({ email: res.data.email, name: res.data.name, phone: res.data.phone, id: res.data.user_id });
      setToken(res.data.access_token || res.data.token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        user: { email: res.data.email, name: res.data.name, phone: res.data.phone, id: res.data.user_id },
        token: res.data.access_token || res.data.token,
        refresh_token: res.data.refresh_token || null,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
      throw e;
    }
  };

  const register = async (email, password, name, phone) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/auth/register`, { email, password, name, phone });
      setUser({ email: res.data.email, name: res.data.name, phone: res.data.phone, id: res.data.user_id });
      setToken(res.data.access_token || res.data.token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        user: { email: res.data.email, name: res.data.name, phone: res.data.phone, id: res.data.user_id },
        token: res.data.access_token || res.data.token,
        refresh_token: res.data.refresh_token || null,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
      throw e;
    }
  };

  const logout = async () => {
    try {
      // Tell backend to blacklist the token
      if (token) {
        await axios.post(`${BACKEND_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
      clearAuth();
      toast.success('Logged out');
    } catch (error) {
      console.error('Logout error:', error);
      clearAuth();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
